/* global $, APP, interfaceConfig */

import Logger from 'meet-hour-logger';

import {
    createScreenSharingIssueEvent,
    sendAnalytics
} from '../../../react/features/analytics';
import { setCustomParticipantVisible } from '../../../react/features/base/conference';
import { getPictureInPictureModeConfig } from '../../../react/features/base/config/functions.any';
import { isMobileBrowser } from '../../../react/features/base/environment/utils';
import { MEDIA_TYPE } from '../../../react/features/base/media';
import { VIDEO_TYPE } from '../../../react/features/base/media/constants';
import { FakeParticipant } from '../../../react/features/base/mtApi';
import {
    getAllParticipantAndSharedFrames,
    getLocalParticipant,
    getParticipantById,
    getPinnedParticipant,
    getVirtualScreenshareParticipantOwnerId,
    pinParticipant
} from '../../../react/features/base/participants';
import {
    getLocalVideoTrack,
    getTrackByMediaTypeAndParticipant
} from '../../../react/features/base/tracks';
import {
    exitTileViewIfNeeded } from '../../../react/features/conference/filmStripViewManager';
import {
    isVideoPlayable
} from '../../../react/features/filmstrip';
import {
    LAYOUTS,
    shouldDisplayTileView
} from '../../../react/features/video-layout';
import {
    ZOOM_SYNC_EPSILON,
    flipZoomStateX,
    getLocalVideoZoom,
    getRemoteVideoZoom,
    toTransform
} from '../../../react/features/zoom-sync';


const logger = Logger.getLogger(__filename);

/**
 * Display mode constant used when video is being displayed on the small video.
 *
 * @type {number}
 * @constant
 */
const DISPLAY_VIDEO = 0;

/**
 * Display mode constant used when the user's avatar is being displayed on
 * the small video.
 *
 * @type {number}
 * @constant
 */
const DISPLAY_AVATAR = 1;

/**
 * Display mode constant used when neither video nor avatar is being displayed
 * on the small video. And we just show the display name.
 *
 * @type {number}
 * @constant
 */
const DISPLAY_BLACKNESS_WITH_NAME = 2;

/**
 * Display mode constant used when video is displayed and display name
 * at the same time.
 *
 * @type {number}
 * @constant
 */
const DISPLAY_VIDEO_WITH_NAME = 3;

/**
 * Display mode constant used when neither video nor avatar is being displayed
 * on the small video. And we just show the display name.
 *
 * @type {number}
 * @constant
 */
const DISPLAY_AVATAR_WITH_NAME = 4;

/**
 *
 */
export default class SmallVideo {
    /**
   * Timeout used to detect double tapping.
   * It is active while user has tapped once.
   */
    _firstTap: number;

    /**
   * Constructor.
   */
    constructor() {
        this.videoIsHovered = false;
        this.videoType = undefined; // 'camera' or 'desktop'
        this.mediaType = undefined; // MEDIA_TYPE.VIDEO or MEDIA_TYPE.SCREENSHARE
        this.isScreenSharing = false; // Whether this is a screenshare
        this.isCameraVideo = false; // Whether this is a camera video
        this.displayMode = null; // Track current display mode
        this.lastDisplayModeInput = null; // Track last input to avoid unnecessary updates
        this.lastTileStripViewClass = null; // Track tile view class
        this._lastMediaClassSignature = null;

        // Initialize tracking variables for state changes
        this._prevParticipantsLength = 0;
        this._prevConferenceView = '';

        // Bind event handlers so they are only bound once for every instance.
        this.updateView = this.updateView.bind(this);

        this._onContainerClick = this._onContainerClick.bind(this);
        this._clearDoubleClickTimeout = this._clearDoubleClickTimeout.bind(this);
        this._showTileViewDoubleClickTooltip = this._showTileViewDoubleClickTooltip.bind(this);
        this._hideTileViewDoubleClickTooltip = this._hideTileViewDoubleClickTooltip.bind(this);
    }

    /**
   * Returns the identifier of this small video.
   *
   * @returns The identifier of this small video.
   */
    getId() {
        return this.id;
    }

    /**
   * Indicates if this small video is currently visible.
   *
   * @returns <tt>true</tt> if this small video isn't currently visible and
   * <tt>false</tt> - otherwise.
   */
    isVisible() {
        return this.$container.is(':visible');
    }

    /**
   * Configures hoverIn/hoverOut handlers. Depends on connection indicator.
   */
    bindHoverHandler() {
        // Add hover handler
        this.$container.hover(
            () => {
                this.videoIsHovered = true;
                this.renderThumbnail(true);
                this.updateView();
            },
            () => {
                this.videoIsHovered = false;
                this.renderThumbnail(false);
                this.updateView();
                this._hideTileViewDoubleClickTooltip();
            }
        );

        // Trigger initial update without hover
        this.updateView();

        // Reduced frequency and only update if state actually changed
        this.autoUpdateInterval = setInterval(() => {
            this.updateViewIfChanged();
        }, 1000); // Reduced from 500ms to 1000ms
    }

    /**
   * Only update view if something actually changed
   */
    updateViewIfChanged() {
        const state = APP.store.getState();
        const _participantsLength = this.participantsLength();
        const { iAmRecorder } = state['features/base/config'];
        const displayHeaderInRecorder = state['features/base/config']?.interfaceConfig?.displayHeaderInRecorder;

        // Changed logic to handle undefined (default to true)
        const shouldDisplayHeader = displayHeaderInRecorder === undefined || displayHeaderInRecorder === true;
        const conferenceView = iAmRecorder
            ? shouldDisplayHeader ? state['features/base/conference'].conferenceView : 'noheader'
            : state['features/base/conference'].conferenceView;

        const chatopen = state['features/chat'].isOpen || state['features/participants-pane'].isOpen;
        const displayModeInput = this.computeDisplayModeInput();

        // Create a serializable version without circular references
        // Exclude videoTrack and connectionStatus as they may contain circular references
        const serializableInput = Object.fromEntries(
            Object.entries(displayModeInput).filter(([ k ]) => k !== 'videoTrack' && k !== 'connectionStatus')
        );
        const inputString = JSON.stringify(serializableInput);

        // Check if display mode input changed
        const inputChanged = this.lastDisplayModeInputString !== inputString;

        // Track previous values to detect changes
        // Store current values for next comparison
        this._prevParticipantsLength = _participantsLength;
        this._prevConferenceView = conferenceView;
        this._prevChatOpen = chatopen;

        // Detect changes in participants count, conference view, or chatopen


        if (inputChanged) {
            this.lastDisplayModeInputString = inputString;
            this.updateView();
        }
    }

    /**
   * Renders the thumbnail.
   */
    renderThumbnail() {
        // Should be implemented by in subclasses.
    }

    /**
   * This is an especially interesting function. A naive reader might think that
   * it returns this SmallVideo's "video" element. But it is much more exciting.
   * It first finds this video's parent element using jquery, then uses a utility
   * from lib-meet-hour to extract the video element from it (with two more
   * jquery calls), and finally uses jquery again to encapsulate the video element
   * in an array. This last step allows (some might prefer "forces") users of
   * this function to access the video element via the 0th element of the returned
   * array (after checking its length of course!).
   */
    selectVideoElement() {
        return $($(this.container).find('video')[0]);
    }

    /**
       * Enables / disables the css responsible for focusing/pinning a video
       * thumbnail. This method now delegates to the state-based logic in updateView().
       *
       * @param isFocused - Indicates if the thumbnail should be focused/pinned or not.
       * @deprecated This method is kept for backward compatibility. The focus state
       * is now automatically managed based on participant.pinned and participant.moderatorPin
       * in the updateView() method.
       */
    focus(isFocused) {
        // For backward compatibility, we still support manual focus calls
        // but the primary logic is now in updateView() based on participant state
        const focusedCssClass = 'videoContainerFocused';
        const isFocusClassEnabled = this.$container.hasClass(focusedCssClass);

        // Only apply manual focus if it doesn't conflict with state-based pinning
        const state = APP.store.getState();
        const participant = getParticipantById(state, this.id);
        const isPinned = participant?.pinned || false;
        const isModeratorPinned = participant?.moderatorPin || false;
        const shouldBeFocusedByState = isPinned || isModeratorPinned;

        // If state indicates this should be focused/unfocused, let updateView handle it
        // Otherwise, apply the manual focus request
        if (!shouldBeFocusedByState) {
            if (!isFocused && isFocusClassEnabled) {
                this.$container.removeClass(focusedCssClass);
            } else if (isFocused && !isFocusClassEnabled) {
                this.$container.addClass(focusedCssClass);
            }
        }
    }

    /**
   *
   */
    hasVideo() {
        return this.selectVideoElement().length !== 0;
    }

    /**
   * Checks whether the user associated with this <tt>SmallVideo</tt> is currently
   * being displayed on the "large video".
   *
   * @returns {boolean} <tt>true</tt> if the user is displayed on the large video
   * or <tt>false</tt> otherwise.
   */
    isCurrentlyOnLargeVideo() {
        return (
            APP.store.getState()['features/large-video']?.participantId === this.id
        );
    }

    /**
   * Checks whether there is a playable video stream available for the user
   * associated with this <tt>SmallVideo</tt>.
   *
   * @returns {boolean} <tt>true</tt> if there is a playable video stream available
   * or <tt>false</tt> otherwise.
   */
    isVideoPlayable() {
        return isVideoPlayable(APP.store.getState(), this.id);
    }

    /**
   * Returns the number of participants in the conference.
   * @returns {number} The number of participants in the conference.
   */
    participantsLength() {
        const state = APP.store.getState();
        const { iAmRecorder } = state['features/base/config'];
        const { tileViewTileLimit, currentTileViewPage } = state['features/filmstrip'];

        const numberOfParticipants = getAllParticipantAndSharedFrames(state)?.length || 0;
        const _tileViewTileLimit = Math.max(tileViewTileLimit || 1, 1);

        const totalPages = Math.max(1, Math.ceil(numberOfParticipants / _tileViewTileLimit));

        const currentParticipantsPage = Math.min(Math.max(currentTileViewPage || 1, 1), totalPages);

        const startIndex = (currentParticipantsPage - 1) * _tileViewTileLimit;

        const participantsInCurrentPage = Math.max(0, Math.min(
            _tileViewTileLimit,
            numberOfParticipants - startIndex
        ));

        if (iAmRecorder) {
            return APP.store.getState()['features/base/participants'].length - 1;
        }


        return participantsInCurrentPage;
    }

    /**
   * Determines what should be display on the thumbnail.
   *
   * @returns {number} One of <tt>DISPLAY_VIDEO</tt>,<tt>DISPLAY_AVATAR</tt>
   * or <tt>DISPLAY_BLACKNESS_WITH_NAME</tt>.
   */
    selectDisplayMode(input) {
        // When user is on large video (and not in tile view), show appropriate display
        if (input.isCurrentlyOnLargeVideo && !input.tileViewActive) {
            // Display name is always and only displayed when user is on the stage
            return input.isVideoPlayable && !input.isAudioOnly
                ? DISPLAY_BLACKNESS_WITH_NAME
                : DISPLAY_AVATAR_WITH_NAME;
        }

        // For screensharing (desktop/SCREENSHARE media type), show video if available and playable
        // This includes both fake screenshare participants and regular participants sharing screen
        if (input.isScreenSharing && input.isVideoPlayable && input.hasVideo && !input.isAudioOnly) {
            // Show video with name on hover, otherwise just video
            return input.isHovered ? DISPLAY_VIDEO_WITH_NAME : DISPLAY_VIDEO;
        }

        // For regular camera video (camera/VIDEO media type), show video if available and playable
        // This is the standard participant camera feed
        if (input.isCameraVideo && input.isVideoPlayable && input.hasVideo && !input.isAudioOnly) {
            // Show video with name on hover, otherwise just video
            return input.isHovered ? DISPLAY_VIDEO_WITH_NAME : DISPLAY_VIDEO;
        }

        // When tile view is not active and no video is available, show avatar (with name on hover)
        if (!input.tileViewActive) {
            return input.isHovered ? DISPLAY_AVATAR_WITH_NAME : DISPLAY_AVATAR;
        }

        // Default: show avatar with name on hover when no video is available
        return input.isHovered ? DISPLAY_AVATAR_WITH_NAME : DISPLAY_AVATAR;
    }

    /**
   * Computes information that determine the display mode.
   *
   * @returns {Object}
   */
    computeDisplayModeInput() {
        let isScreenSharing = false;
        let isCameraVideo = false;
        let videoType = null;
        let mediaType = null;
        let connectionStatus;
        const state = APP.store.getState();
        const id = this.id;
        const participant = getParticipantById(state, id);
        const isLocal = participant?.local ?? true;
        const isFakeScreenshare = participant?.isFakeParticipant === FakeParticipant.RemoteScreenShare
            || participant?.isFakeParticipant === FakeParticipant.LocalScreenShare;

        // Check if this is a special fake participant (YouTube, Etherpad, Whiteboard)
        // These have isFakeParticipant === true (boolean), not a string
        const isSpecialFakeParticipant = participant?.isFakeParticipant === true;

        const tracks = state['features/base/tracks'];

        // Determine which track to use based on participant type
        let videoTrack;

        // Don't try to get video tracks for special fake participants (YouTube, Etherpad, Whiteboard)
        if (isSpecialFakeParticipant) {
            // These don't have video tracks - they render their own content
            videoTrack = null;
            mediaType = null;
        } else if (isFakeScreenshare) {
            // For fake screenshare participants, always get SCREENSHARE media type
            videoTrack = getTrackByMediaTypeAndParticipant(tracks, MEDIA_TYPE.SCREENSHARE, id);
            mediaType = MEDIA_TYPE.SCREENSHARE;
        } else if (isLocal) {
            // For local participant (non-screenshare), get VIDEO media type
            videoTrack = getLocalVideoTrack(tracks);
            mediaType = MEDIA_TYPE.VIDEO;
        } else {
            // For remote participants (non-screenshare), get VIDEO media type
            videoTrack = getTrackByMediaTypeAndParticipant(tracks, MEDIA_TYPE.VIDEO, id);
            mediaType = MEDIA_TYPE.VIDEO;
        }

        // Determine video type and whether it's screensharing or camera
        if (videoTrack) {
            videoType = videoTrack.videoType; // 'desktop' or 'camera'

            // Check if this is screensharing based on multiple conditions
            if (isFakeScreenshare || videoTrack.videoType === 'desktop' || mediaType === MEDIA_TYPE.SCREENSHARE) {
                isScreenSharing = !videoTrack.muted;
                isCameraVideo = false;
            } else if (videoTrack.videoType === 'camera' || mediaType === MEDIA_TYPE.VIDEO) {
                isScreenSharing = false;
                isCameraVideo = !videoTrack.muted;
            }
        }

        // Get connection status for remote participants
        if (
            typeof participant !== 'undefined'
            && (!participant?.isFakeParticipant || isFakeScreenshare)
            && !participant?.local
        ) {
            connectionStatus = participant.connectionStatus;
        }

        return {
            isCurrentlyOnLargeVideo: this.isCurrentlyOnLargeVideo(),
            isHovered: this._isHovered(),
            isAudioOnly: APP.conference.isAudioOnly(),
            tileViewActive: shouldDisplayTileView(state),
            isVideoPlayable: this.isVideoPlayable(),
            hasVideo: Boolean(this.selectVideoElement().length),
            connectionStatus,
            canPlayEventReceived: this._canPlayEventReceived,
            videoStream: Boolean(videoTrack),
            isScreenSharing,
            isCameraVideo,
            videoType,
            mediaType,
            isFakeScreenshare,
            videoStreamMuted: videoTrack ? videoTrack.muted : 'no stream',
            videoTrack // Include the actual track for debugging
        };
    }

    /**
   * Checks whether current video is considered hovered. Currently it is hovered
   * if the mouse is over the video, or if the connection
   * indicator is shown(hovered).
   *
   * @private
   */
    _isHovered() {
        return this.videoIsHovered;
    }

    /**
   * Gets the current video track information for this participant.
   *
   * @returns {Object} Object containing videoType, mediaType, and track details
   */
    getVideoTrackInfo() {
        const state = APP.store.getState();
        const participant = getParticipantById(state, this.id);
        const isLocal = participant?.local ?? true;
        const isFakeScreenshare = participant?.isFakeParticipant === FakeParticipant.RemoteScreenShare
            || participant?.isFakeParticipant === FakeParticipant.LocalScreenShare;
        const tracks = state['features/base/tracks'];

        let videoTrack;
        let mediaType;

        if (isFakeScreenshare) {
            videoTrack = getTrackByMediaTypeAndParticipant(tracks, MEDIA_TYPE.SCREENSHARE, this.id);
            mediaType = MEDIA_TYPE.SCREENSHARE;
        } else if (isLocal) {
            videoTrack = getLocalVideoTrack(tracks);
            mediaType = MEDIA_TYPE.VIDEO;
        } else {
            videoTrack = getTrackByMediaTypeAndParticipant(tracks, MEDIA_TYPE.VIDEO, this.id);
            mediaType = MEDIA_TYPE.VIDEO;
        }

        return {
            videoType: videoTrack?.videoType, // 'camera' or 'desktop'
            mediaType, // MEDIA_TYPE.VIDEO or MEDIA_TYPE.SCREENSHARE
            videoTrack,
            isScreenSharing: videoTrack?.videoType === 'desktop' || mediaType === MEDIA_TYPE.SCREENSHARE,
            isCameraVideo: videoTrack?.videoType === 'camera' || (mediaType === MEDIA_TYPE.VIDEO && !isFakeScreenshare),
            isFakeScreenshare,
            muted: videoTrack?.muted
        };
    }

    /**
   * Updates the css classes of the thumbnail based on the current state.
   */
    updateView() {
        const state = APP.store.getState();
        const displayModeInput = this.computeDisplayModeInput();


        // Get participant data to check pinning state
        // Handle local participant specially as it might have different ID handling
        let participant = getParticipantById(state, this.id);

        if (!participant) {
            // If not found, check if this is the local participant
            const localParticipant = getLocalParticipant(state);

            if (localParticipant && localParticipant.id === this.id) {
                participant = localParticipant;
            }
        }

        const isPinned = participant?.pinned || false;
        const isModeratorPinned = participant?.moderatorPin || false;

        // Store video type information in instance for later reference
        this.videoType = displayModeInput.videoType;
        this.mediaType = displayModeInput.mediaType;
        this.isScreenSharing = displayModeInput.isScreenSharing;
        this.isCameraVideo = displayModeInput.isCameraVideo;

        // Update videoContainerFocused class based on pinning state
        const focusedCssClass = 'videoContainerFocused';
        const shouldBeFocused = isPinned || isModeratorPinned;
        const isFocusClassEnabled = this.$container.hasClass(focusedCssClass);

        if (!shouldBeFocused && isFocusClassEnabled) {
            this.$container.removeClass(focusedCssClass);
        } else if (shouldBeFocused && !isFocusClassEnabled) {
            this.$container.addClass(focusedCssClass);
        }

        // Determine whether video, avatar or blackness should be displayed
        const newDisplayMode = this.selectDisplayMode(displayModeInput);


        // Update media-related classes only when the signature changes to avoid
        // expensive class churn and browser repaints on unrelated app actions.
        const mediaClassSignature = [
            displayModeInput.isScreenSharing ? 'screen' : 'noscreen',
            displayModeInput.isCameraVideo ? 'camera' : 'nocamera',
            displayModeInput.videoType || 'novideotype',
            displayModeInput.mediaType || 'nomediatype'
        ].join('|');

        if (this._lastMediaClassSignature !== mediaClassSignature) {
            this.$container.removeClass('video-type-screenshare video-type-desktop video-type-camera media-type-screenshare media-type-video');

            if (displayModeInput.isScreenSharing) {
                this.$container.addClass('video-type-screenshare video-type-desktop media-type-screenshare');
            } else if (displayModeInput.isCameraVideo) {
                this.$container.addClass('video-type-camera media-type-video');
            }

            if (displayModeInput.videoType) {
                this.$container.addClass(`video-type-${displayModeInput.videoType}`);
            }

            if (displayModeInput.mediaType) {
                this.$container.addClass(`media-type-${displayModeInput.mediaType}`);
            }

            this._lastMediaClassSignature = mediaClassSignature;
        }

        // Only update if display mode actually changed
        if (this.displayMode !== newDisplayMode) {
            // Remove old display classes
            this.$container.removeClass((index, classNames) =>
                classNames.split(' ').filter(name => name.startsWith('display-'))
            );


            // Remove old display name classes
            this.$container.removeClass('display-avatar-with-name display-name-on-black display-name-on-video');

            this.displayMode = newDisplayMode;
            let displayModeString = '';

            switch (this.displayMode) {
            case DISPLAY_AVATAR_WITH_NAME:
                displayModeString = 'avatar-with-name';
                this.$container.addClass('display-avatar-with-name');
                break;
            case DISPLAY_BLACKNESS_WITH_NAME:
                displayModeString = 'blackness-with-name';
                this.$container.addClass('display-name-on-black');
                break;
            case DISPLAY_VIDEO:
                displayModeString = 'video';
                this.$container.addClass('display-name-on-video');
                break;
            case DISPLAY_VIDEO_WITH_NAME:
                displayModeString = 'video-with-name';
                this.$container.addClass('display-name-on-video');
                break;
            case DISPLAY_AVATAR:
            default:
                displayModeString = 'avatar';
                this.$container.addClass('display-avatar-with-name');
                break;
            }

            // Create a serializable version for logging without circular references
            // Exclude videoTrack and connectionStatus to avoid circular references
            const loggableInput = Object.fromEntries(
                Object.entries(displayModeInput).filter(([ k ]) => k !== 'videoTrack' && k !== 'connectionStatus')
            );

            logger.debug(
                `Displaying ${displayModeString} for ${this.id
                }, videoType: ${displayModeInput.videoType}, mediaType: ${displayModeInput.mediaType
                }, isScreenSharing: ${displayModeInput.isScreenSharing}, isCameraVideo: ${displayModeInput.isCameraVideo
                }, data: [${JSON.stringify(loggableInput)}]`
            );
        }

        // Handle tile view CSS variables and classes

        if (
            this.displayMode !== DISPLAY_VIDEO
      && this.displayMode !== DISPLAY_VIDEO_WITH_NAME
      && displayModeInput.tileViewActive
      && displayModeInput.isScreenSharing
      && !displayModeInput.isAudioOnly
        ) {
            // send the event
            sendAnalytics(
                createScreenSharingIssueEvent({
                    source: 'thumbnail',
                    ...displayModeInput
                })
            );
        }

        this.updateZoomTransform();
    }

    /**
     * Returns the zoom state for this thumbnail's video.
     *
     * @returns {Object|undefined}
     */
    getZoomState() {
        const state = APP.store?.getState?.();

        if (!state) {
            return undefined;
        }

        if (this.isLocal) {
            return getLocalVideoZoom(state, this.videoType || VIDEO_TYPE.CAMERA);
        }

        if (!this.id) {
            return undefined;
        }

        // Determine if this thumbnail represents screenshare or camera
        const isScreenShare = this.mediaType === MEDIA_TYPE.SCREENSHARE
            || this.videoType === VIDEO_TYPE.DESKTOP
            || this.isScreenSharing
            || this.isFakeScreenshare
            || (this.user && typeof this.user.isScreenSharing === 'function' && this.user.isScreenSharing());

        const videoType = isScreenShare ? VIDEO_TYPE.DESKTOP : VIDEO_TYPE.CAMERA;
        const ownerId = getVirtualScreenshareParticipantOwnerId(this.id) || this.id;

        return getRemoteVideoZoom(state, ownerId, videoType);
    }

    /**
     * Initializes a ResizeObserver on the container to recalculate zoom transform on geometry changes.
     */
    initResizeObserver() {
        if (typeof ResizeObserver !== 'undefined' && this.container && !this._resizeObserver) {
            this._resizeObserver = new ResizeObserver(() => {
                if (!this._disposed) {
                    this.updateZoomTransform();
                }
            });
            this._resizeObserver.observe(this.container);
        }
    }

    /**
     * Requests an update of the video element CSS transform based on the current zoom state.
     * Throttled to animation frames to prevent layout thrashing.
     */
    updateZoomTransform() {
        if (this._disposed || !this.container) {
            return;
        }

        if (this._zoomTransformFrame) {
            return;
        }

        this._zoomTransformFrame = requestAnimationFrame(() => {
            this._zoomTransformFrame = null;
            this._applyZoomTransform();
        });
    }

    /**
     * Calculates and applies the CSS transform to the video element.
     *
     * @private
     */
    _applyZoomTransform() {
        if (this._disposed || !this.container) {
            return;
        }

        const videoEl = this.container.querySelector('video');

        if (!videoEl) {
            return;
        }

        const state = APP.store?.getState?.();

        if (!state) {
            return;
        }

        const zoomState = this.getZoomState();
        const isScreenShare = this.mediaType === MEDIA_TYPE.SCREENSHARE
            || this.videoType === VIDEO_TYPE.DESKTOP
            || this.isScreenSharing
            || this.isFakeScreenshare
            || (this.user && typeof this.user.isScreenSharing === 'function' && this.user.isScreenSharing());
        const isCamera = !isScreenShare;
        const isLocal = Boolean(this.isLocal || (APP.conference && APP.conference.isLocalId(this.id)));
        const isFlipped = Boolean(
            isLocal
            && isCamera
            && (
                videoEl.classList.contains('flipVideoX')
                || state['features/base/settings']?.localFlipX
                || this.localFlipX
            )
        );

        if (!zoomState || !(zoomState.zoom > 1 + ZOOM_SYNC_EPSILON)) {
            const defaultTransform = isFlipped ? 'scale(-1, 1)' : '';

            if (videoEl.style.transform !== defaultTransform) {
                videoEl.style.transform = defaultTransform;
            }
            if (videoEl.style.transition) {
                videoEl.style.transition = '';
            }

            return;
        }

        const boxWidth = this.container.offsetWidth || videoEl.offsetWidth || 0;
        const boxHeight = this.container.offsetHeight || videoEl.offsetHeight || 0;

        if (boxWidth <= 0 || boxHeight <= 0) {
            return;
        }

        const box = {
            width: boxWidth,
            height: boxHeight
        };
        const { filmStripView, objectFit } = state['features/base/conference'] || {};
        const currentObjectFit = videoEl.style.objectFit
            || (filmStripView === LAYOUTS.TILE_VIEW ? objectFit || 'cover' : 'cover');

        const displayed = isFlipped ? flipZoomStateX(zoomState) : zoomState;
        const transform = toTransform(displayed, box, currentObjectFit);

        if (transform.zoom > 1 + ZOOM_SYNC_EPSILON) {
            const flipStr = isFlipped ? ' scaleX(-1)' : '';

            // For local participant and camera video thumbnails, keep zoom centered to prevent the video tag from moving aside.
            const posX = isLocal || isCamera ? 0 : transform.posX;
            const posY = isLocal || isCamera ? 0 : transform.posY;
            const newTransform = `translate3d(${posX}px, ${posY}px, 0) scale(${transform.zoom})${flipStr}`;

            if (videoEl.style.transform !== newTransform) {
                videoEl.style.transform = newTransform;
            }
            if (videoEl.style.transformOrigin !== '50% 50%') {
                videoEl.style.transformOrigin = '50% 50%';
            }
            if (videoEl.style.transition !== 'transform 0.2s ease-out') {
                videoEl.style.transition = 'transform 0.2s ease-out';
            }
        } else {
            const defaultTransform = isFlipped ? 'scale(-1, 1)' : '';

            if (videoEl.style.transform !== defaultTransform) {
                videoEl.style.transform = defaultTransform;
            }
            if (videoEl.style.transition) {
                videoEl.style.transition = '';
            }
        }
    }

    /**
   * Shows or hides the dominant speaker indicator.
   *
   * @param show - Whether to show or hide.
   */
    showDominantSpeakerIndicator(show) {
        // Don't create and show dominant speaker indicator if
        // DISABLE_DOMINANT_SPEAKER_INDICATOR is true
        if (interfaceConfig.DISABLE_DOMINANT_SPEAKER_INDICATOR) {
            return;
        }

        if (!this.container) {
            logger.warn(
                `Unable to set dominant speaker indicator - ${this.videoSpanId} does not exist`
            );

            return;
        }

        this.$container.toggleClass('active-speaker', show);
    }

    /**
   * Initalizes any browser specific properties. Currently sets the overflow
   * property for Qt browsers on Windows to hidden, thus fixing the following
   * problem:
   * Some browsers don't have full support of the object-fit property for the
   * video element and when we set video object-fit to "cover" the video
   * actually overflows the boundaries of its container, so it's important
   * to indicate that the "overflow" should be hidden.
   *
   * Setting this property for all browsers will result in broken audio levels,
   * which makes this a temporary solution, before reworking audio levels.
   */
    initBrowserSpecificProperties() {
        this.initResizeObserver();

        const userAgent = window.navigator.userAgent;

        if (
            userAgent.indexOf('QtWebEngine') > -1
      && (userAgent.indexOf('Windows') > -1 || userAgent.indexOf('Linux') > -1)
        ) {
            this.$container.css('overflow', 'hidden');
        }
    }

    /**
   * Cleans up components on {@code SmallVideo} and removes itself from the DOM.
   *
   * @returns {void}
   */
    remove() {
        logger.log('Remove thumbnail', this.id);

        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = null;
        }

        if (this._zoomTransformFrame) {
            cancelAnimationFrame(this._zoomTransformFrame);
            this._zoomTransformFrame = null;
        }

        // Clear the auto-update interval
        if (this.autoUpdateInterval) {
            clearInterval(this.autoUpdateInterval);
            this.autoUpdateInterval = null;
        }

        this._hideTileViewDoubleClickTooltip();
        this._unmountThumbnail();

        // Remove whole container
        if (this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }
    }

    /**
   * Alternative method: Trigger view update on state changes
   * Call this method whenever you want to force an update
   */
    forceUpdateView() {
        this.updateView();
    }

    /**
   * Force immediate update when participants count or conference view changes
   * This method should be called from external components when state changes
   */
    onStateChange() {
        const state = APP.store.getState();
        const _participantsLength = this.participantsLength();
        const { iAmRecorder } = state['features/base/config'];
        const displayHeaderInRecorder = state['features/base/config'].interfaceConfig.displayHeaderInRecorder;

        const shouldDisplayHeader = displayHeaderInRecorder === undefined || displayHeaderInRecorder === true;
        const conferenceView = iAmRecorder
            ? shouldDisplayHeader ? state['features/base/conference'].conferenceView : 'noheader'
            : state['features/base/conference'].conferenceView;

        // Update tracking variables
        this._prevParticipantsLength = _participantsLength;
        this._prevConferenceView = conferenceView;

        // Also trigger a full view update to ensure everything is in sync
        this.updateView();
    }

    /**
   * Helper function for re-rendering multiple react components of the small
   * video.
   *
   * @returns {void}
   */
    rerender() {
        this.updateView();
    }

    /**
   * Clears the first click timeout (mobile tap or desktop tile-view double-click).
   *
   * @returns {void}
   */
    _clearDoubleClickTimeout() {
        window.clearTimeout(this._firstTap);
        this._firstTap = undefined;
        window.clearTimeout(this._firstClick);
        this._firstClick = undefined;
        this._hideTileViewDoubleClickTooltip();
    }

    /**
   * Callback invoked when the thumbnail is clicked and potentially trigger
   * pinning of the participant.
   *
   * @param {MouseEvent} event - The click event to intercept.
   * @private
   * @returns {void}
   */
    _onContainerClick(event) {
        const isMobile = isMobileBrowser();

        if (isMobile && !this._firstTap) {
            this._firstTap = window.setTimeout(this._clearDoubleClickTimeout, 300);

            return;
        }

        const contextMenu = document.querySelector('.contexify');

        if (contextMenu && contextMenu.offsetParent !== null) {
            return;
        }

        const triggerPin = this._shouldTriggerPin(event);

        if (!triggerPin || (event.target && event.target.closest && event.target.closest('[data-ignore-pin]'))) {
            return false;
        }

        // On desktop, when in tile view, require a double-click to switch views.
        // In Picture-in-Picture mode, double-click logic should not work (allow single click).
        if (!isMobile) {
            const state = APP.store.getState();
            const isPiP = getPictureInPictureModeConfig(state);
            const filmStripView = state['features/base/conference'].filmStripView;
            const isTileView = filmStripView === LAYOUTS.TILE_VIEW;

            if (!isPiP && isTileView && !this._firstClick) {
                this._firstClick = window.setTimeout(() => {
                    this._firstClick = undefined;
                }, 300);

                this._showTileViewDoubleClickTooltip();

                return false;
            }
            window.clearTimeout(this._firstClick);
            this._firstClick = undefined;
            this._hideTileViewDoubleClickTooltip();
        }

        if (event.stopPropagation) {
            event.stopPropagation();
            event.preventDefault();
        }

        this.togglePin();

        return false;
    }

    /**
     * Shows a tooltip inside the thumbnail container indicating that
     * double-clicking is required to change the view.
     *
     * @private
     * @returns {void}
     */
    _showTileViewDoubleClickTooltip() {
        if (!this.container) {
            return;
        }

        SmallVideo._hideActiveDoubleClickTooltip();

        let text = 'Double click to change the view';

        try {
            if (typeof APP !== 'undefined' && APP.translation && typeof APP.translation.translateString === 'function') {
                const translated = APP.translation.translateString('videothumbnail.doubleClickToChangeView');

                if (translated && translated !== 'videothumbnail.doubleClickToChangeView') {
                    text = translated;
                }
            }
        } catch (e) {
            // fallback to default text
        }

        const tooltip = document.createElement('div');

        tooltip.className = 'tile-view-double-click-tooltip';
        tooltip.innerText = text;

        this.container.appendChild(tooltip);
        SmallVideo._activeTooltipElement = tooltip;

        // Trigger entrance animation on next frame
        requestAnimationFrame(() => {
            if (tooltip.parentNode) {
                tooltip.classList.add('visible');
            }
        });

        // Auto-hide after 2000ms
        SmallVideo._activeTooltipTimeout = window.setTimeout(() => {
            SmallVideo._hideActiveDoubleClickTooltip();
        }, 2000);
    }

    /**
     * Hides and removes the currently active double-click tooltip.
     *
     * @private
     * @returns {void}
     */
    _hideTileViewDoubleClickTooltip() {
        SmallVideo._hideActiveDoubleClickTooltip();
    }

    /**
     * Hides and removes the currently active double-click tooltip across all containers.
     *
     * @static
     * @private
     * @returns {void}
     */
    static _hideActiveDoubleClickTooltip() {
        if (SmallVideo._activeTooltipTimeout) {
            window.clearTimeout(SmallVideo._activeTooltipTimeout);
            SmallVideo._activeTooltipTimeout = undefined;
        }

        const tooltips = document.querySelectorAll('.tile-view-double-click-tooltip');

        tooltips.forEach(el => {
            el.classList.remove('visible');
            el.classList.add('fade-out');
            window.setTimeout(() => {
                if (el.parentNode) {
                    el.parentNode.removeChild(el);
                }
            }, 200);
        });

        SmallVideo._activeTooltipElement = undefined;
    }

    /**
   * Returns whether or not a click event is targeted at certain elements which
   * should not trigger a pin.
   *
   * @param {MouseEvent} event - The click event to intercept.
   * @private
   * @returns {boolean}
   */
    _shouldTriggerPin(event) {
        // TODO Checking the classes is a workround to allow events to bubble into
        // the DisplayName component if it was clicked. React's synthetic events
        // will fire after jQuery handlers execute, so stop propogation at this
        // point will prevent DisplayName from getting click events. This workaround
        // should be removeable once LocalVideo is a React Component because then
        // the components share the same eventing system.
        const $source = $(event.target || event.srcElement);

        return (
            $source.parents('.displayNameContainer').length === 0
      && $source.parents('.popover').length === 0
      && !event.target.classList.contains('popover')
        );
    }

    /**
   * Pins the participant displayed by this thumbnail or unpins if already pinned.
   *
   * @returns {void}
   */
    togglePin() {
        const state = APP.store.getState();
        const pinnedParticipant = getPinnedParticipant(state);
        const filmStripView = state['features/base/conference'].filmStripView;

        const isTileView = filmStripView === LAYOUTS.TILE_VIEW;
        const isSameParticipantPinned = pinnedParticipant?.id === this.id;

        const isPiP = getPictureInPictureModeConfig(state);

        // In desktop non-PiP, exit tile view when pinning. In PiP, allow user to stay in tile view.
        if (!isPiP) {
            exitTileViewIfNeeded(APP.store.dispatch, filmStripView);
        }

        // If in TILE VIEW and clicking on already pinned participant → do nothing (or unpin in PiP)
        if (isTileView && isSameParticipantPinned) {
            if (isPiP) {
                APP.store.dispatch(pinParticipant(null, false));
                APP.store.dispatch(setCustomParticipantVisible(false));
                setTimeout(() => {
                    this.updateView();
                }, 10);
            }

            return;
        }

        // If in TILE VIEW and clicking on another participant → pin new participant
        if (isTileView && !isSameParticipantPinned) {
            APP.store.dispatch(pinParticipant(this.id, true));
            APP.store.dispatch(setCustomParticipantVisible(false));
            setTimeout(() => {
                this.updateView();
            }, 10);

            return;
        }

        // NON-TILE VIEW behavior
        // Clicking on same participant → unpin
        // Clicking on different participant → pin new one
        const participantIdToPin = isSameParticipantPinned ? null : this.id;

        APP.store.dispatch(pinParticipant(participantIdToPin, participantIdToPin !== null));
        APP.store.dispatch(setCustomParticipantVisible(false));

        // Force update tile view classes after pin action
        setTimeout(() => {
            this.updateView();
        }, 10);
    }


    /**
   * Unmounts the thumbnail.
   */
    _unmountThumbnail() {
        // Use modern React 18 approach instead of deprecated ReactDOM.unmountComponentAtNode
        // Handle both naming conventions: 'root' (LocalVideo) and 'reactRoot' (RemoteVideo)
        // Clear the reference BEFORE unmounting. unmount() flushes React
        // synchronously and that commit can re-enter renderThumbnail() via store
        // subscribers or hover handlers; if the reference still pointed at this
        // root, that re-entrant render() would target a root being torn down and
        // throw "Cannot update an unmounted root".
        if (this.root) {
            const root = this.root;

            this.root = null;
            root.unmount();
        } else if (this.reactRoot) {
            const reactRoot = this.reactRoot;

            this.reactRoot = null;
            reactRoot.unmount();
        } else if (this.container) {
            // Fallback for cases where root wasn't created properly
            this.container.innerHTML = '';
        }
    }
}
